import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, firstValueFrom, forkJoin, Observable, of, take } from 'rxjs';
import { getOnlineStatus } from '../shared/utils/date.util';
import { AlarmRuleDto, UpdateAlarmRuleDeviceDto } from '../types/dtos/alarm-rule.dto';
import { AlarmDto } from '../types/dtos/alarm.dto';
import { BaseFilterRequestDto, BaseResponseDto } from '../types/dtos/base.dto';
import {
  DeviceAlarmRuleResponseDto,
  DeviceDto,
  DeviceMeasurementRequestDto,
  DeviceResponseDto,
  DevicesResponseDto,
  DeviceUpdateDTO,
  DeviceUpdateImageDTO,
  DistrictMeasurementRequestDto,
  DistrictMeasurementResponseDto,
  DistrictResponseDto,
  MeasurementResponseDto,
  PositionDto,
  WaterMeasurementRangeDto,
} from '../types/dtos/device.dto';
import { DeviceProfileDto } from '../types/dtos/deviceProfile.dto';
import { WebsocketResponseDto } from '../types/dtos/websocket.dto';
import { DomainEnum } from '../types/enums/websocket.enum';
import { MixedChartInterface } from '../types/interfaces/charts.interface';
import { AlarmService } from './alarm.service';
import { AuthService } from './auth.service';
import { BaseService } from './base.service';
import { DeviceProfileService } from './device-profile.service';
import { WebsocketService } from './websocket.service';

@Injectable({
  providedIn: 'root',
})
export class DeviceService extends BaseService {
  private readonly _url: string = `${ this._baseUrl }/device`;
  private rawDevicesSubject: BehaviorSubject<DeviceDto[]> =
    new BehaviorSubject<DeviceDto[]>([]);

  private devicesSubject: BehaviorSubject<DeviceDto[]> =
    new BehaviorSubject<DeviceDto[]>([]);
  public devices$: Observable<DeviceDto[]> = this.devicesSubject.asObservable();

  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _websocketService: WebsocketService,
    private readonly _alarmService: AlarmService,
    private readonly _authService: AuthService,
    private readonly _deviceProfileService: DeviceProfileService,
  ) {
    super();
    this._init()
      .then();
  }

  private _waterMeasurementData: BehaviorSubject<MixedChartInterface> = new BehaviorSubject<MixedChartInterface>({
    loading: true,
    labels: [],
    datasets: [
      {
        label: 'Wasserverbrauch (m³)',
        type: 'line',
        borderWidth: 1,
        borderColor: 'rgb(75, 192, 192)',
        data: [],
        fill: true,
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        tension: 0.3,
        yAxisID: 'y',
        pointRadius: 0,
        pointHoverRadius: 0,
        pointHitRadius: 0,
      },
      {
        label: 'Wasserverbrauch (pro Tag)',
        type: 'bar',
        backgroundColor: 'rgba(255, 99, 132, 0.5)',
        data: [],
        borderWidth: 1,
        borderColor: 'rgb(255, 99, 132)',
        yAxisID: 'y1',
      },
    ],
  });

  public waterMeasurementData$: Observable<MixedChartInterface> = this._waterMeasurementData.asObservable();

  public set waterMeasurementData(data: MixedChartInterface) {
    this._waterMeasurementData.next(data);
  }

  private _waterMeasurementRange: BehaviorSubject<WaterMeasurementRangeDto | null> =
    new BehaviorSubject<WaterMeasurementRangeDto | null>(null);

  public waterMeasurementRange$: Observable<WaterMeasurementRangeDto | null> =
    this._waterMeasurementRange.asObservable();

  public set waterMeasurementRange(data: WaterMeasurementRangeDto | null) {
    this._waterMeasurementRange.next(data);
  }

  public get latestMeasurementDateTime(): string {
    const latest = this.devicesSubject.value
      .map((device: DeviceDto) => device.last_measurement_received_time)
      .filter((date): date is string => !!date && !isNaN(new Date(date).getTime()))
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0];

    return latest ?? '';
  }

  public getOnlineStatus(device: DeviceDto): 'green' | 'orange' | 'red' {
    return getOnlineStatus(device);
  }

  public getAll(query: BaseFilterRequestDto = {}): Observable<DevicesResponseDto> {
    return this._httpClient
      .get<DevicesResponseDto>(`${ this._url }s`, {
        params: this.createQueryParams(query),
      })
      .pipe(take(1));
  }

  public fetchDevice(id: number): Observable<DeviceResponseDto> {
    return this._httpClient
      .get<DeviceResponseDto>(`${ this._url }/${ id }`)
      .pipe(take(1));
  }

  public fetchDevices(): void {
    this.getAll({
      limit: -1,
    })
      .subscribe((devices: DevicesResponseDto) => {
        this.rawDevicesSubject.next(devices.data ?? []);
      });
  }

  public getLocalById(deviceId: number): DeviceDto | null {
    return this.rawDevicesSubject.value.find((device: DeviceDto) => device.id === deviceId) ?? null;
  }

  public getDeviceIdByEui(device_eui: string): number | undefined {
    const device: DeviceDto | undefined = this.rawDevicesSubject.value.find((device: DeviceDto) => device.device_eui === device_eui)
    return device ? device.id : undefined;
  }

  public removeDeviceFromList(deviceId: number): void {
    const devices: DeviceDto[] = this.rawDevicesSubject.value.filter((device: DeviceDto) => device.id !== deviceId);
    this.rawDevicesSubject.next(devices);
  }

  public async getById(deviceId: number): Promise<DeviceDto | null> {
    let device: DeviceDto | undefined = this.devicesSubject.value.find(
      (currentDevice: DeviceDto) => currentDevice.id === deviceId,
    );

    if (device?.is_full) return device;

    try {
      const result: DeviceResponseDto = await firstValueFrom(
        this.fetchDevice(deviceId),
      );

      if (!result.data) return null;

      device = {
        ...result.data,
        is_full: true,
      };

      this._updateDeviceInSubject(deviceId, device);

      return device;
    } catch (error: unknown) {
      console.error('Error fetching device:', error);
      return null;
    }
  }

  public updateDevice(
    deviceId: number,
    deviceUpdate: DeviceUpdateDTO,
  ): Observable<BaseResponseDto> {
    return this._httpClient.put<BaseResponseDto>(
      `${ this._baseUrl }/device/${ deviceId }`,
      deviceUpdate,
    )
      .pipe(take(1));
  }

  public archiveDevice(
    deviceId: number,
  ): Observable<BaseResponseDto> {
    return this._httpClient.delete<BaseResponseDto>(
      `${ this._baseUrl }/device/${ deviceId }`,
      {},
    )
      .pipe(take(1));
  }

  public getDeviceMeasurements(
    deviceId: number,
    options: DeviceMeasurementRequestDto,
  ): Observable<MeasurementResponseDto> {
    let params: HttpParams = new HttpParams();

    for (const [key, value] of Object.entries(options)) {
      if (value !== undefined && value !== null) {
        params = params.set(key, String(value));
      }
    }

    return this._httpClient.get<MeasurementResponseDto>(
      `${ this._baseUrl }/device/${ deviceId }/measurements`,
      {
        params,
      },
    )
      .pipe(take(1));
  }

  public getWaterMeasurementValueKey(device: DeviceDto): string {
    let key: string = 'METER_VALUE_1';

    if (device.device_profile_id) {
      const deviceProfile: DeviceProfileDto | null = this._deviceProfileService.getDeviceProfileById(device.device_profile_id);
      if (deviceProfile?.primary_value) {
        key = deviceProfile.primary_value;
      }
    }

    return key;
  }

  public updateMeasurement(
    deviceId: number,
  ): Observable<MeasurementResponseDto> {
    return this._httpClient.put<MeasurementResponseDto>(
      `${ this._baseUrl }/device/${ deviceId }/measurements`,
      {},
    )
      .pipe(take(1));
  }

  public getDeviceAlarmRules(
    deviceId: number,
  ): Observable<DeviceAlarmRuleResponseDto> {
    return this._httpClient.get<DeviceAlarmRuleResponseDto>(
      `${ this._baseUrl }/device/${ deviceId }/alarmRule`,
    )
      .pipe(take(1));
  }

  public getDeviceAlarmRuleById(
    deviceId: number,
    ruleId: number,
  ): Observable<BaseResponseDto<AlarmRuleDto>> {
    return this._httpClient.get<BaseResponseDto<AlarmRuleDto>>(
      `${ this._baseUrl }/device/${ deviceId }/alarmRule/${ ruleId }`,
    )
      .pipe(take(1));
  }

  public addAlarmRuleToDevice(
    deviceId: number,
    alarmRuleId: number,
  ): Observable<BaseResponseDto> {
    return this._httpClient.post<BaseResponseDto>(
      `${ this._baseUrl }/device/${ deviceId }/alarmRule/${ alarmRuleId }`,
      {},
    )
      .pipe(take(1));
  }

  public updateDeviceAlarmRule(
    deviceId: number,
    alarmRuleId: number,
    update: UpdateAlarmRuleDeviceDto,
  ): Observable<BaseResponseDto<AlarmRuleDto>> {
    return this._httpClient.put<BaseResponseDto<AlarmRuleDto>>(
      `${ this._baseUrl }/device/${ deviceId }/alarmRule/${ alarmRuleId }`,
      update,
    )
      .pipe(take(1));
  }

  public removeAlarmRuleFromDevice(
    deviceId: number,
    alarmRuleId: number,
  ): Observable<BaseResponseDto> {
    return this._httpClient.delete<BaseResponseDto>(
      `${ this._baseUrl }/device/${ deviceId }/alarmRule/${ alarmRuleId }`,
      {},
    )
      .pipe(take(1));
  }

  public getDataMeasurements(
    options: DeviceMeasurementRequestDto,
  ): Observable<MeasurementResponseDto> {
    let params: HttpParams = new HttpParams();

    for (const [key, value] of Object.entries(options)) {
      if (value !== undefined && value !== null) {
        params = params.set(key, String(value));
      }
    }

    return this._httpClient.get<MeasurementResponseDto>(
      `${ this._baseUrl }/devices/measurements`, {
        params,
      })
      .pipe(take(1));
  }

  public getDeviceDistricts(): Observable<DistrictResponseDto> {
    return this._httpClient.get<DistrictResponseDto>(
      `${ this._baseUrl }/device/districts`,
    )
      .pipe(take(1));
  }

  public getDistrictMeasurements(
    options: DistrictMeasurementRequestDto,
  ): Observable<DistrictMeasurementResponseDto> {
    let params: HttpParams = new HttpParams();

    for (const [key, value] of Object.entries(options)) {
      if (value !== undefined && value !== null) {
        params = params.set(key, String(value));
      }
    }

    return this._httpClient.get<DistrictMeasurementResponseDto>(
      `${ this._baseUrl }/devices/measurements/district`, {
        params,
      })
      .pipe(take(1));
  }

  public updateDeviceList(deviceList: DeviceDto[]): void {
    this.rawDevicesSubject.next(deviceList);
  }

  public uploadDeviceImage(
    deviceId: number,
    data: DeviceUpdateImageDTO,
  ): Observable<BaseResponseDto[]> {
    const requests: Observable<BaseResponseDto>[] = [];

    if (data.newImages?.length) {
      const formData: FormData = new FormData();
      data.newImages.forEach((file) => formData.append('image', file));

      requests.push(
        this._httpClient.post<BaseResponseDto>(
          `${ this._baseUrl }/device/${ deviceId }/image`,
          formData,
        )
          .pipe(take(1)),
      );
    }

    if (data.deleteIds?.length) {
      requests.push(
        this._httpClient.delete<BaseResponseDto>(
          `${ this._baseUrl }/device/${ deviceId }/image/${ data.deleteIds.join(',') }`,
        )
          .pipe(take(1)),
      );
    }

    if (!requests.length) {
      return of([]);
    }

    return forkJoin(requests);
  }

  public getCenter(): [number, number] {
    let maxX: number = 0;
    let minX: number = 200;
    let maxY: number = 0;
    let minY: number = 200;

    let devices: DeviceDto[] = this.rawDevicesSubject.value;
    const devicePositions: PositionDto[] = devices.map((device: DeviceDto) => device.position)
      .filter((position: PositionDto | undefined): position is PositionDto => position !== undefined);

    if (!devicePositions.length) {
      return [
        49.8153,
        6.1296,
      ];
    }

    for (const position of devicePositions) {
      maxX = Math.max(maxX, position.longitude);
      minX = Math.min(minX, position.longitude);
      maxY = Math.max(maxY, position.latitude);
      minY = Math.min(minY, position.latitude);
    }

    return [
      (maxY + minY) / 2,
      (maxX + minX) / 2,
    ];
  }

  public getDeviceNumber(deviceId: number): string | undefined {
    let device: DeviceDto | null = this.getLocalById(deviceId);
    if (device === null) return undefined;

    return device.serial_number ?? device.device_eui;
  }

  public getDeviceNumberByDevice(device: DeviceDto): string {
    return device.serial_number ?? device.device_eui ?? '-';
  }

  private async _init() {
    await this._authService.waitUntilLogin();
    this._listenToDeviceUpdates();
    this._listenToAlarmUpdate();
  }

  private _listenToAlarmUpdate(): void {
    combineLatest([
      this.rawDevicesSubject.asObservable(),
      this._alarmService.unResolvedAlarms$,
    ])
      .subscribe(([devices, alarms]: [DeviceDto[], AlarmDto[]]) => {
        const devicesWithAlarms: DeviceDto[] = devices.map((device: DeviceDto) => {
          const deviceAlarms: AlarmDto[] = alarms.filter(
            (alarm: AlarmDto) => alarm.device_id === device.id,
          );

          return {
            ...device,
            alarms: deviceAlarms,
          };
        });
        this.devicesSubject.next(devicesWithAlarms);
      });
  }

  private _listenToDeviceUpdates(): void {
    this._websocketService.subscribe(
      DomainEnum.DEVICE,
      'update',
      (payload: WebsocketResponseDto) => {
        if (payload.params && payload.params['id']) {
          this._updateDevice(Number(payload.params['id']));
          return;
        }

        this.getAll({limit: -1})
          .subscribe({
            next: (result: DevicesResponseDto) => {
              this.updateDeviceList(result.data ?? []);
            },
            error: (error: unknown) => {
              console.error('Error fetching devices:', error);
            },
          });
      },
    );
  }

  private _updateDevice(deviceId: number): void {
    this.fetchDevice(deviceId)
      .subscribe({
        next: (result: DeviceResponseDto) => {
          if (!result.data) {
            return;
          }

          const device: DeviceDto = {
            ...result.data,
            is_full: true,
          };

          this._updateDeviceInSubject(deviceId, device);
        },
        error: (error: unknown) => {
          console.error('Error fetching device:', error);
        },
      });
  }

  private _updateDeviceInSubject(id: number, device: DeviceDto): void {
    const currentDevices: DeviceDto[] = [...this.rawDevicesSubject.value];
    const deviceIndex: number = currentDevices.findIndex(
      (currentDevice: DeviceDto) => currentDevice.id === id,
    );

    if (deviceIndex !== -1) {
      currentDevices[deviceIndex] = device;
    } else {
      currentDevices.push(device);
    }

    this.rawDevicesSubject.next(currentDevices);
  }
}
